/**
 * Модуль для загрузки файлов в Supabase Storage через TUS протокол
 * @module tusFileUploader
 *
 * @description
 * TUS (The Upload Server) - протокол для возобновляемых загрузок.
 * Преимущества TUS:
 * - Chunked upload (загрузка по частям)
 * - Возобновление после обрыва соединения
 * - Стабильная скорость при больших файлах
 *
 * Требования к серверу (self-hosted Supabase):
 * - TUS_URL_PATH: '/storage/v1/upload/resumable'
 * - REQUEST_ALLOW_X_FORWARDED_PATH: 'true'
 * - KONG_PORT_MAPS: "443:8000"
 */

import * as tus from "tus-js-client";
import { supabase } from "@/integrations/supabase/client";

/**
 * Ответ после успешной загрузки файла
 */
interface UploadResponse {
    url: string;
    key: string;
}

/**
 * Конфигурация для TUS загрузчика
 */
interface TusUploaderOptions {
    supabaseUrl: string;
    supabaseKey: string;
    supabaseBucket: string;
    /** 
     * Размер chunk в байтах.
     * По умолчанию 50MB для ускорения загрузки.
     * Если сеть нестабильна, можно уменьшить до 6MB.
     */
    chunkSize?: number;
}

/**
 * Размер chunk по умолчанию: 6MB.
 * ВАЖНО: Supabase Storage (TUS) требует ровно 6MB — все чанки, кроме последнего,
 * должны быть 6MB, иначе загрузка рвётся на нестабильных/медленных каналах (VPN, мобильный).
 * Не увеличивать. См. https://supabase.com/docs/guides/storage/uploads/resumable-uploads
 */
const DEFAULT_CHUNK_SIZE = 6 * 1024 * 1024;

/**
 * Получает актуальный JWT токен сессии для авторизации
 */
async function getSessionToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
        throw new Error("Необходима авторизация для загрузки файлов");
    }

    return session.access_token;
}

/**
 * Создает TUS-based загрузчик файлов для Supabase Storage
 */
export const createTusFileUploader = ({
    supabaseUrl,
    supabaseKey,
    supabaseBucket,
    chunkSize = DEFAULT_CHUNK_SIZE,
}: TusUploaderOptions) => {
    /**
     * Загружает файл через TUS протокол
     * @param file - Файл для загрузки
     * @param onProgress - Callback для отслеживания прогресса (0-100)
     * @param abortSignal - Сигнал для отмены загрузки
     */
    const uploadFile = async (
        file: File,
        onProgress: (progress: number) => void,
        abortSignal?: AbortSignal
    ): Promise<UploadResponse> => {
        const fileExt = file.name.split(".").pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = fileName;

        // Получаем актуальный токен сессии пользователя
        const accessToken = await getSessionToken();

        // TUS endpoint для Supabase Storage
        const tusEndpoint = `${supabaseUrl}/storage/v1/upload/resumable`;

        return new Promise((resolve, reject) => {
            // Проверяем, не отменена ли загрузка до начала
            if (abortSignal?.aborted) {
                reject(new Error("Загрузка отменена"));
                return;
            }

            onProgress(0);

            const upload = new tus.Upload(file, {
                endpoint: tusEndpoint,
                retryDelays: [0, 1000, 3000, 5000], // Retry после обрыва
                chunkSize: chunkSize,

                headers: {
                    authorization: `Bearer ${accessToken}`, // Токен пользователя
                    apikey: supabaseKey,                    // Anon Key (для доступа к API gateway)
                    "x-upsert": "true",
                },

                uploadDataDuringCreation: true,
                removeFingerprintOnSuccess: true,

                metadata: {
                    bucketName: supabaseBucket,
                    objectName: filePath,
                    contentType: file.type,
                    cacheControl: "3600",
                },

                onError: (error) => {
                    console.error("TUS upload error:", error);
                    if ('originalRequest' in error && (error as unknown as { originalRequest?: { getURL(): string } }).originalRequest) {
                        const req = (error as unknown as { originalRequest: { getURL(): string } }).originalRequest;
                        console.error("Failed request URL:", req.getURL());
                    }
                    reject(new Error(`Ошибка загрузки: ${error.message}`));
                },

                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = (bytesUploaded / bytesTotal) * 100;
                    onProgress(percentage);
                },

                onSuccess: () => {
                    // Получаем публичный URL загруженного файла
                    const {
                        data: { publicUrl },
                    } = supabase.storage.from(supabaseBucket).getPublicUrl(filePath);

                    onProgress(100);

                    resolve({
                        url: publicUrl,
                        key: filePath,
                    });
                },
            });

            // Обработка отмены загрузки
            if (abortSignal) {
                abortSignal.addEventListener("abort", () => {
                    upload.abort(true); // true = удалить частично загруженный файл
                    reject(new Error("Загрузка отменена"));
                });
            }

            // Проверяем наличие незавершенных загрузок и пытаемся возобновить
            upload.findPreviousUploads().then((previousUploads) => {
                if (previousUploads.length > 0) {
                    upload.resumeFromPreviousUpload(previousUploads[0]);
                }
                upload.start();
            });
        });
    };

    return { uploadFile };
};

/**
 * Фабричная функция для обратной совместимости
 */
export const createFileUploader = createTusFileUploader;
